import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { ListItemRendererComponent } from 'gung-list';
import { AiOrderParserItem, AI_ORDER_REVIEW_ROUTE } from '../../../models/ai-order-parser-item';
import { AiOrderParserBatchListConfigService } from '../../../services/ai-order-parser-review/ai-order-parser-batch-list-config.service';
import { AiOrderParserReviewService } from '../../../services/ai-order-parser-review/ai-order-parser-review.service';

/**
 * One row in the order inbox (REDESIGN.md §3): a real anchor spanning seven
 * columns — status pill, from, document, config, merged order number, row
 * count and relative received time — with the verb carried by the status pill.
 */
@Component({
  selector: 'lib-pdf-ai-parser-order-card',
  templateUrl: './pdf-ai-parser-order-card.component.html',
  styleUrls: ['./pdf-ai-parser-order-card.component.scss'],
  standalone: false
})
export class AiOrderParserOrderCardComponent extends ListItemRendererComponent<AiOrderParserItem> implements OnInit {
  @ViewChild('deleteModal') deleteModalTpl!: TemplateRef<unknown>;
  @ViewChild('submitModal') submitModalTpl!: TemplateRef<unknown>;

  private translate = inject(TranslateService);
  private listConfig = inject(AiOrderParserBatchListConfigService);
  private modalService = inject(NgbModal);
  private notificationService = inject(GungNotificationService);
  private reviewService = inject(AiOrderParserReviewService);
  private router = inject(Router);

  /** True while the submit-to-ERP call is in flight, so the button can lock and show a spinner. */
  isSubmitting = false;

  /**
   * Whether the item arrived on the latest poll — captured once at render so
   * later change detection cycles don't replay the arrival highlight.
   */
  isNew = false;

  ngOnInit(): void {
    this.isNew = !!this.data?.id && this.listConfig.isNewItem(this.data.id);
  }

  get reviewLink(): any[] {
    return ['/' + AI_ORDER_REVIEW_ROUTE, this.data?.id];
  }

  /**
   * Swallow a click so the row's routerLink (and the native <a> href) don't fire. Used by the
   * overflow (⋯) toggle, which lives inside the row anchor.
   */
  stopRowNavigation(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * "Open" overflow-menu action. The menu is rendered on the document body (container="body"),
   * so its clicks don't reach the row anchor — navigate programmatically instead.
   */
  open(): void {
    if (!this.data?.id) {
      return;
    }
    this.router.navigate(this.reviewLink);
  }

  /** True once the order has an ERP order number, so the dialog can warn it won't delete the ERP order. */
  get hasErpOrder(): boolean {
    return !!this.data?.erpOrderIds?.length;
  }

  /**
   * Open the delete-confirm dialog. Stops the click from bubbling to the row anchor
   * (which would otherwise navigate to the review page).
   */
  askDelete(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.data?.id) {
      return;
    }
    this.modalService.open(this.deleteModalTpl, { centered: true }).result.then(
      () => this.performDelete(),
      () => {
        /* dismissed — nothing to do */
      }
    );
  }

  private performDelete(): void {
    const id = this.data?.id;
    if (!id) {
      return;
    }
    this.reviewService.delete(id).pipe(first()).subscribe({
      next: () => {
        // Drop the row locally for instant feedback (matches the config list's
        // delete). A backend re-fetch here can still return the just-deleted row.
        this.listConfig.removeItem(id);
      },
      error: () => {
        /* delete failed; row remains, which is the signal */
      }
    });
  }

  /**
   * Open the submit-confirm dialog. Posting to the ERP is irreversible, so the listing
   * never submits on a single click. Stops the click from reaching the row anchor.
   */
  askSubmit(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.data?.id) {
      return;
    }
    this.modalService.open(this.submitModalTpl, { centered: true }).result.then(
      () => this.performSubmit(),
      () => {
        /* dismissed — nothing to do */
      }
    );
  }

  /**
   * Post the stored order to the ERP. Unlike the review page's submit, this path does NOT PUT
   * the order first: there is nothing to save from the listing, and `submit-to-erp` posts the
   * order as stored.
   */
  private performSubmit(): void {
    const id = this.data?.id;
    if (!id) {
      return;
    }
    this.isSubmitting = true;
    this.reviewService.submitToErp(id).pipe(first()).subscribe({
      next: item => {
        this.isSubmitting = false;
        const erpIds = item.erpOrderIds || [];
        this.notificationService.notify(
          this.translate.instant('AI_PARSER_TOAST_SUBMITTED_TITLE'),
          erpIds.length
            ? this.translate.instant('AI_PARSER_TOAST_SUBMITTED_BODY', { ids: erpIds.join(', ') })
            : this.translate.instant('AI_PARSER_TOAST_SUBMITTED_PENDING'),
          'success',
          erpIds.length ? { routerLink: ['/orders', erpIds[0]] } : undefined,
          10000
        );
        // Reload so the row picks up its new status and ERP order number.
        this.listConfig.reload();
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        // The backend rejects a failed gate (invalid rows, unresolved customer/price) with a message.
        const serverMessage = typeof err.error === 'string' ? err.error : err.error?.message;
        this.notificationService.notify(
          '',
          serverMessage || this.translate.instant('AI_PARSER_ERROR_SUBMIT'),
          'error'
        );
      }
    });
  }

  // --- Trust counter --------------------------------------------------------

  /** Why the item is in manual review; null unless it is awaiting review for a trust reason. */
  get trustReason(): string | null {
    return this.data?.status === 'AWAITING_REVIEW' ? this.data?.trustStatus?.reason ?? null : null;
  }

  /** Lowest clean-submission count of the order's customers — the one holding the order back. */
  get trustCount(): number {
    const counts = [this.data?.trustStatus?.delivery, this.data?.trustStatus?.billing]
      .filter(counter => !!counter)
      .map(counter => counter!.cleanSubmissions);
    return counts.length ? Math.min(...counts) : 0;
  }

  get trustThreshold(): number {
    return this.data?.trustStatus?.threshold ?? 0;
  }

  /**
   * Whether the row may be submitted straight from the listing. Only when the customer has passed
   * the threshold: inside the trust ramp, or on a periodic check, the order is in review because
   * nobody has looked at it yet, and a listing submit would count an unexamined parse as clean.
   * Requires a resolved counter too — an order with no customer id at all is refused server-side
   * by enforceCustomerIdentity, so offering the button would only produce an error.
   */
  get canQuickSubmit(): boolean {
    const status = this.data?.trustStatus;
    if (this.data?.status !== 'AWAITING_REVIEW' || !status || this.trustReason) {
      return false;
    }
    return !!(status.delivery || status.billing);
  }

  /** Tooltip spelling out the badge; empty when no trust reason applies. */
  get trustBadgeTitle(): string {
    const reason = this.trustReason;
    if (!reason) {
      return '';
    }
    return this.translate.instant(
      reason === 'TRUST_RAMP' ? 'AI_PARSER_TRUST_RAMP_TITLE' : 'AI_PARSER_PERIODIC_CHECK_TITLE',
      { count: this.trustCount, threshold: this.trustThreshold }
    );
  }

  /** Tooltip for the warning icon; empty when there are no warnings. */
  get warningsTitle(): string {
    const n = this.data?.warnings?.length || 0;
    if (!n) {
      return '';
    }
    return this.translate.instant(n === 1 ? 'AI_PARSER_WARNINGS_ONE' : 'AI_PARSER_WARNINGS', { count: n });
  }

  // --- From ---------------------------------------------------------------

  get isEmail(): boolean {
    return !!this.data?.fromEmail;
  }

  /** Sender address for emailed orders, the uploading user for manual uploads. */
  get sender(): string {
    return this.data?.fromEmail || this.data?.uploadedBy || this.translate.instant('AI_PARSER_UPLOADED_MANUALLY');
  }

  /** Tooltip on the source glyph telling how the order entered the queue. */
  get sourceTitle(): string {
    return this.translate.instant(this.isEmail ? 'AI_PARSER_RECEIVED_BY_EMAIL' : 'AI_PARSER_UPLOADED_MANUALLY');
  }

  get subject(): string | undefined {
    return this.data?.subject;
  }

  // --- Document -----------------------------------------------------------

  get fileName(): string {
    return this.data?.originalFilename || '';
  }

  /** Monochrome FA glyph mapped from the filename extension / mime type. */
  get fileIconClass(): string {
    const name = (this.data?.originalFilename || '').toLowerCase();
    const mime = (this.data?.mimeType || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop()! : '';
    if (ext === 'pdf' || mime.includes('pdf')) {
      return 'fa-file-pdf';
    }
    if (['xls', 'xlsx'].includes(ext) || mime.includes('sheet') || mime.includes('excel')) {
      return 'fa-file-excel';
    }
    if (ext === 'csv' || mime.includes('csv')) {
      return 'fa-file-csv';
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'bmp'].includes(ext) || mime.startsWith('image/')) {
      return 'fa-file-image';
    }
    return 'fa-file';
  }

  // --- Order no. (merged column) -------------------------------------------

  /** The ERP order number assigned once the order was posted. */
  get erpOrderNumber(): string | undefined {
    return this.data?.erpOrderIds?.[0];
  }

  /** The customer's own order number from the document. */
  get customerOrderNumber(): string | undefined {
    return this.data?.parsedOrder?.extra?.['customerOrderNumber'];
  }

  /** Which order-number field was just copied, to briefly swap its icon to a check. */
  copiedField: 'customer' | 'erp' | null = null;
  private copyResetTimer?: ReturnType<typeof setTimeout>;

  /** Copy an order number to the clipboard without triggering the row's navigation. */
  copyOrderNumber(value: string | undefined, field: 'customer' | 'erp', event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!value) {
      return;
    }
    navigator.clipboard?.writeText(value).then(() => {
      this.copiedField = field;
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => (this.copiedField = null), 1500);
    });
  }

  get rowCount(): number {
    return this.data?.parsedOrder?.rows?.length ?? 0;
  }

  // --- Received -----------------------------------------------------------

  get receivedAt(): string | undefined {
    return this.data?.receivedAt || this.data?.createdAt;
  }

  /** Coarse relative label ("4h ago"); the absolute timestamp lives in the title attr. */
  get relativeReceived(): string {
    const iso = this.receivedAt;
    if (!iso) {
      return '';
    }
    const then = new Date(iso).getTime();
    if (isNaN(then)) {
      return '';
    }
    const min = Math.floor((Date.now() - then) / 60000);
    if (min < 1) {
      return this.translate.instant('AI_PARSER_TIME_JUST_NOW');
    }
    if (min < 60) {
      return this.translate.instant('AI_PARSER_TIME_MINUTES_AGO', { count: min });
    }
    const hours = Math.floor(min / 60);
    if (hours < 24) {
      return this.translate.instant('AI_PARSER_TIME_HOURS_AGO', { count: hours });
    }
    return this.translate.instant('AI_PARSER_TIME_DAYS_AGO', { count: Math.floor(hours / 24) });
  }
}
